/*
 * WarehouseFever theme - shared storefront behaviour.
 *
 *   WF.cart              Shopify Ajax Cart API wrapper (serialised requests)
 *   WF.toast()           status toast
 *   <quantity-input>     −/+ control that can never hold an invalid value
 *   <variant-form>       add-to-cart form used by product cards and the product page
 *   <facet-form>         collection/search filters, sort and grid/list view
 *   <predictive-search>  header search suggestions
 *   <product-recommendations>
 *
 * Prices are never computed here: any price markup comes from Liquid, which
 * only renders it for logged-in customers.
 */
(() => {
  const WF = (window.WF = window.WF || {});
  const routes = WF.routes || {};
  const strings = WF.strings || {};

  /* ---------------------------------------------------------------- utils */

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  WF.debounce = debounce;

  /* ----------------------------------------------------------------- cart */

  // Every cart mutation goes through one queue so rapid clicks cannot race
  // each other (e.g. two adds reading a stale cart, or change-after-add).
  let queue = Promise.resolve();
  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };

  const request = async (url, body) => {
    const res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(data.description || data.message || strings.addError);
      error.status = res.status;
      throw error;
    }
    return data;
  };

  WF.cart = {
    get: () => request(`${routes.cart}.js`),
    add: (items, sections) =>
      enqueue(() => request(`${routes.cartAdd}.js`, { items, sections })).then(async (data) => {
        await WF.cart.refreshCount();
        return data;
      }),
    change: (key, quantity, sections) =>
      enqueue(() =>
        request(`${routes.cartChange}.js`, {
          id: key,
          quantity,
          sections,
          sections_url: window.location.pathname,
        })
      ).then((cart) => {
        WF.cart.setCount(cart.item_count);
        return cart;
      }),
    clear: (sections) =>
      enqueue(() =>
        request(`${routes.cartClear}.js`, {
          sections,
          sections_url: window.location.pathname,
        })
      ).then((cart) => {
        WF.cart.setCount(cart.item_count);
        return cart;
      }),
    // Resolves once every queued mutation has finished.
    idle: () => queue,
    refreshCount: async () => {
      const cart = await WF.cart.get();
      WF.cart.setCount(cart.item_count);
      return cart;
    },
    setCount: (count) => {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        const changed = el.textContent.trim() !== String(count);
        el.textContent = count;
        if (el.classList.contains('crate__count')) el.hidden = count === 0;
        if (changed) {
          el.classList.remove('is-bumped');
          void el.offsetWidth;
          el.classList.add('is-bumped');
        }
      });
      document.dispatchEvent(new CustomEvent('wf:cart:updated', { detail: { count } }));
    },
  };

  /* ---------------------------------------------------------------- toast */

  let toastTimer;
  WF.toast = (html, { error = false, timeout = 4000 } = {}) => {
    const el = document.getElementById('Toast');
    if (!el) return;
    el.innerHTML = html;
    el.classList.toggle('toast--error', error);
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (el.hidden = true), timeout);
  };

  const escapeHTML = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  WF.escapeHTML = escapeHTML;

  /* ------------------------------------------------------- quantity-input */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input || this.bound) return;
      this.bound = true;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn || btn.disabled) return;
        e.preventDefault();
        this.set(this.value + Number(btn.dataset.step) * this.step, true);
      });
      this.input.addEventListener('change', () => this.set(this.input.value, false));
      this.input.addEventListener('keydown', (e) => {
        // Enter inside a card should add to cart, not reload the page with a bad value.
        if (e.key === 'Enter') this.set(this.input.value, false);
      });
      this.set(this.input.value, false, true);
    }

    get min() {
      const v = parseInt(this.input.min, 10);
      return Number.isFinite(v) ? v : 1;
    }
    get max() {
      const v = parseInt(this.input.max, 10);
      return Number.isFinite(v) && v > 0 ? v : Infinity;
    }
    get step() {
      const v = parseInt(this.input.step, 10);
      return Number.isFinite(v) && v > 0 ? v : 1;
    }
    get value() {
      return parseInt(this.input.value, 10) || 0;
    }

    // Clamp to [min, max] and snap to the step; never leaves a blank/NaN value.
    normalize(raw) {
      let v = parseInt(raw, 10);
      if (!Number.isFinite(v)) v = this.min;
      v = Math.max(this.min, Math.min(this.max, v));
      if (this.step > 1 && v % this.step !== 0) {
        const up = Math.ceil(v / this.step) * this.step;
        v = up <= this.max ? up : Math.floor(v / this.step) * this.step;
      }
      return Math.max(this.min, v);
    }

    set(raw, emit = true, silent = false) {
      const next = this.normalize(raw);
      const changed = String(next) !== this.input.value;
      this.input.value = next;
      this.querySelector('[data-step="-1"]').disabled = next <= this.min;
      this.querySelector('[data-step="1"]').disabled = next + this.step > this.max;
      if (!silent && (changed || emit)) {
        this.input.dispatchEvent(new Event('qty:change', { bubbles: true }));
      }
    }

    setRules({ min = 1, max = null, step = 1 } = {}) {
      this.input.min = min;
      this.input.step = step;
      if (max) this.input.max = max;
      else this.input.removeAttribute('max');
      this.set(this.input.value, false, true);
    }

    reset() {
      this.set(this.min, false, true);
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* --------------------------------------------------------- variant-form */

  // Wraps a product form. data-mode="card" uses a single variant <select>;
  // data-mode="product" uses one <select> per option and syncs the URL/media.
  class VariantForm extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-button]');
      this.qty = this.querySelector('quantity-input');
      this.error = this.querySelector('[data-form-error]');
      this.scope = this.closest('[data-product-scope], .card') || this;
      const json = this.querySelector('script[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : null;

      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-variant-select], [data-option-index]')) this.onVariantChange();
      });
      this.addEventListener('click', (e) => {
        const preset = e.target.closest('[data-qty-set]');
        if (!preset || !this.qty) return;
        this.qty.set(Number(preset.dataset.qtySet), true);
        this.querySelectorAll('[data-qty-set]').forEach((b) => b.setAttribute('aria-pressed', String(b === preset)));
      });
    }

    get idInput() {
      return this.form.querySelector('[name="id"]');
    }

    currentVariant() {
      if (!this.variants) return null;
      const options = [...this.querySelectorAll('[data-option-index]')];
      if (options.length) {
        const selected = options.map((el) => el.value);
        return this.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
      }
      const id = Number(this.idInput.value);
      return this.variants.find((v) => v.id === id) || null;
    }

    onVariantChange() {
      const variant = this.currentVariant();
      this.hideError();
      if (options(this).length) {
        // Product page: the hidden id input follows the option selects.
        this.idInput.value = variant ? variant.id : '';
        if (variant && this.dataset.mode === 'product') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      }
      this.render(variant);
    }

    render(variant) {
      const sku = this.scope.querySelector('[data-sku]');
      if (sku) {
        sku.textContent = variant?.sku || '';
        sku.closest('.sku').hidden = !variant?.sku;
      }
      const spec = this.scope.querySelector('[data-sku-spec]');
      if (spec) spec.textContent = variant?.sku || '—';
      const price = this.scope.querySelector('[data-price]');
      if (price && variant && variant.price_html) {
        price.outerHTML = variant.price_html;
      }
      if (this.button) {
        const ok = !!variant && variant.available;
        this.button.disabled = !ok;
        this.button.textContent = !variant ? strings.unavailable : ok ? strings.addToCart : strings.soldOut;
      }
      if (this.qty && variant) this.qty.setRules(variant);
      if (variant?.featured_media_id && this.dataset.mode === 'product') {
        this.scope.dispatchEvent(
          new CustomEvent('wf:media:select', { bubbles: true, detail: { id: variant.featured_media_id } })
        );
      }
    }

    async onSubmit(e) {
      e.preventDefault();
      if (this.busy || !this.button || this.button.disabled) return;
      const id = Number(this.idInput.value);
      if (!id) return;
      // Re-validate whatever is typed in the box before sending it.
      if (this.qty) this.qty.set(this.qty.input.value, false, true);
      const quantity = this.qty ? this.qty.value : 1;
      if (quantity < 1) return;

      this.busy = true;
      this.hideError();
      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      try {
        const data = await WF.cart.add([{ id, quantity }]);
        const item = data.items?.[0];
        const title = item ? item.product_title : '';
        WF.toast(
          `${escapeHTML(strings.added.replace('[quantity]', quantity).replace('[title]', title))}
           <a href="${routes.cart}">${escapeHTML(strings.viewCart)}</a>`
        );
        if (this.qty) this.qty.reset();
        this.querySelectorAll('[data-qty-set]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
        this.flashAdded(quantity);
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
        const variant = this.currentVariant();
        this.button.disabled = variant ? !variant.available : false;
      }
    }

    flashAdded(quantity) {
      const btn = this.button;
      if (!this.idleLabel) this.idleLabel = btn.textContent;
      btn.classList.add('is-added');
      btn.textContent = (strings.addedShort || 'Added').replace('[quantity]', quantity);
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = this.idleLabel;
      }, 1600);
    }

    showError(message) {
      if (!this.error) return WF.toast(escapeHTML(message), { error: true });
      this.error.textContent = message;
      this.error.hidden = false;
    }
    hideError() {
      if (this.error) this.error.hidden = true;
    }
  }
  const options = (form) => form.querySelectorAll('[data-option-index]');
  customElements.define('variant-form', VariantForm);

  /* ---------------------------------------------------------- media gallery */

  document.addEventListener('wf:media:select', (e) => {
    const gallery = e.target.querySelector?.('product-gallery');
    gallery?.show(e.detail.id);
  });

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-media-thumb]');
        if (thumb) this.show(Number(thumb.dataset.mediaThumb));
      });
    }
    show(id) {
      const slide = this.querySelector(`[data-media-id="${id}"]`);
      if (!slide) return;
      this.querySelectorAll('[data-media-id]').forEach((el) => (el.hidden = el !== slide));
      this.querySelectorAll('[data-media-thumb]').forEach((el) =>
        el.setAttribute('aria-current', String(Number(el.dataset.mediaThumb) === id))
      );
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------ facet-form */

  const VIEW_KEY = 'wf:collection-view';
  const readView = () => {
    try {
      return localStorage.getItem(VIEW_KEY) || 'grid';
    } catch {
      return 'grid';
    }
  };
  const applyView = (view) => {
    document.querySelectorAll('[data-product-grid]').forEach((grid) => (grid.dataset.view = view));
    document.querySelectorAll('.view-toggle__btn').forEach((b) =>
      b.setAttribute('aria-pressed', String(b.dataset.view === view))
    );
  };

  class FacetForm extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      const submit = debounce(() => form.requestSubmit ? form.requestSubmit() : form.submit(), 350);
      form.addEventListener('change', (e) => {
        if (e.target.matches('input[type="checkbox"], select')) submit();
      });
      form.addEventListener('submit', () => {
        // Drop empty params so URLs stay clean.
        form.querySelectorAll('input[type="number"]').forEach((i) => {
          if (i.value === '') i.disabled = true;
        });
      });
      this.querySelectorAll('.view-toggle__btn').forEach((btn) =>
        btn.addEventListener('click', () => {
          applyView(btn.dataset.view);
          try {
            localStorage.setItem(VIEW_KEY, btn.dataset.view);
          } catch {}
        })
      );
      applyView(readView());
      // Close other open filter dropdowns when one opens.
      this.querySelectorAll('details.facet').forEach((d) =>
        d.addEventListener('toggle', () => {
          if (d.open) this.querySelectorAll('details.facet[open]').forEach((o) => o !== d && (o.open = false));
        })
      );
    }
  }
  customElements.define('facet-form', FacetForm);

  /* ----------------------------------------------------- predictive-search */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      this.input.addEventListener('focus', () => this.input.value.trim() && this.open());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close();
          this.input.focus();
        }
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close();
      });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) return this.close();
      if (this.cache.has(q)) return this.show(this.cache.get(q));
      this.controller?.abort();
      this.controller = new AbortController();
      const url =
        `${routes.predictiveSearch}?q=${encodeURIComponent(q)}` +
        '&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search';
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return this.close();
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('#PredictiveResults');
        const markup = html ? html.innerHTML : '';
        this.cache.set(q, markup);
        this.show(markup);
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      }
    }
    show(markup) {
      this.results.innerHTML = markup;
      markup ? this.open() : this.close();
    }
    open() {
      if (!this.results.innerHTML.trim()) return;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------ product-recommendations */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        const res = await fetch(this.dataset.url);
        const text = await res.text();
        const html = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('product-recommendations');
        if (html && html.innerHTML.trim()) this.innerHTML = html.innerHTML;
        else this.closest('.shopify-section')?.remove();
      };
      new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        load().catch(() => {});
      }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------ sticky-buy */

  // Mobile: a slim Add bar appears once the order panel scrolls out of view.
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const panel = document.querySelector('.order-panel');
      const form = panel?.querySelector('form');
      const button = panel?.querySelector('[data-add-button]');
      if (!panel || !form || !button) return;
      const mine = this.querySelector('[data-sticky-add]');
      mine.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
      const sync = () => {
        mine.disabled = button.disabled;
        mine.textContent = button.textContent;
        mine.classList.toggle('is-added', button.classList.contains('is-added'));
      };
      new MutationObserver(sync).observe(button, { attributes: true, childList: true, characterData: true, subtree: true });
      sync();
      new IntersectionObserver(([entry]) => {
        // Show only after the panel has scrolled up past the top of the screen.
        this.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(panel);
    }
  }
  customElements.define('sticky-buy', StickyBuy);

  /* ------------------------------------------------------------- crate-stack */

  // Hero promo stack: the front crate is dealt to the back every few seconds.
  class CrateStack extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('.crate-card')];
      this.dots = [...this.querySelectorAll('.crate-stack__dots li')];
      if (this.cards.length < 2) return;
      this.active = 0;
      this.render();
      this.querySelector('[data-crate-next]')?.addEventListener('click', () => this.next(true));
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const ms = Number(this.dataset.interval) || 5000;
      if (!reduce) {
        this.timer = setInterval(() => {
          if (!this.paused && !document.hidden && !this.classList.contains('is-offscreen')) this.next();
        }, ms);
        this.addEventListener('pointerenter', () => (this.paused = true));
        this.addEventListener('pointerleave', () => (this.paused = false));
        this.addEventListener('focusin', () => (this.paused = true));
        this.addEventListener('focusout', () => (this.paused = false));
      }
    }
    next(user) {
      this.active = (this.active + 1) % this.cards.length;
      this.render();
      if (user) this.cards[this.active].focus({ preventScroll: true });
    }
    render() {
      const n = this.cards.length;
      this.cards.forEach((card, i) => {
        const pos = (i - this.active + n) % n;
        card.style.setProperty('--pos', pos);
        card.dataset.pos = pos;
        const front = pos === 0;
        if (front) card.removeAttribute('aria-hidden');
        else card.setAttribute('aria-hidden', 'true');
        card.tabIndex = front ? 0 : -1;
      });
      this.dots.forEach((d, i) => d.classList.toggle('is-on', i === this.active));
    }
  }
  customElements.define('crate-stack', CrateStack);

  /* ------------------------------------------------------------ product-rail */

  class ProductRail extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('.rail__track');
      const root = this.closest('.rail') || this;
      const step = () => Math.max(track.clientWidth * 0.8, 240);
      root.querySelector('[data-rail-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      root.querySelector('[data-rail-next]')?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    }
  }
  customElements.define('product-rail', ProductRail);

  /* ------------------------------------------------- reveal, count-up, "/" key */

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const countUp = (el) => {
    const target = Number(el.dataset.countUp);
    if (!target || reduceMotion) return;
    const start = performance.now();
    const fmt = new Intl.NumberFormat();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / 1100);
      el.textContent = fmt.format(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        entry.target.querySelectorAll('[data-count-up]').forEach(countUp);
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  // Endless animations (ticker, brand marquee, hero crates) pause while scrolled out of view.
  const offscreenObserver = new IntersectionObserver((entries) =>
    entries.forEach((entry) => entry.target.classList.toggle('is-offscreen', !entry.isIntersecting))
  );
  const initReveal = () => {
    document.querySelectorAll('[data-reveal]:not(.is-in)').forEach((el) => {
      if (reduceMotion) el.classList.add('is-in');
      else revealObserver.observe(el);
    });
    document.querySelectorAll('.ticker, .marquee, crate-stack').forEach((el) => offscreenObserver.observe(el));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReveal);
  else initReveal();

  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.target.closest && e.target.closest('input, textarea, select, [contenteditable]')) return;
    const input = document.querySelector('[data-search-shortcut]');
    if (!input) return;
    e.preventDefault();
    input.focus();
    input.select();
  });

  /* ---------------------------------------------------------------- header */

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-menu][open]').forEach((d) => {
      d.open = false;
      d.querySelector('summary')?.focus();
    });
  });
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-menu][open]').forEach((d) => {
      if (!d.contains(e.target)) d.open = false;
    });
  });
})();
